import { CheckCircleOutlined } from '@ant-design/icons'
import { Button, Form, Input } from 'antd'
import { noop } from 'lodash-es'
import { FunctionComponent, useState } from 'react'
import styled from 'styled-components'
import { AppContext } from '@/api/executor/app-context'
import { gtagApi } from '@/api/gtag/gtag-api'
import { lineTagTracking } from '@/api/line/line-tag'
import { twitterPixelTracking } from '@/api/twitter/twtitter-pixel'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { BasicModal } from '@/components/common/antd/basic-modal'
import PlanConfigProvider from '@/components/common/plan-config-provider'
import { H3 } from '@/components/common/typography'
import { useAuth } from '@/hooks/use-auth'
import useIsJapan from '@/hooks/use-is-japan'
import { useIntl } from '@/i18n/hooks/use-intl'
import { reduxAPI } from '@/store/redux-api'
import { YearBuySchema } from '@/types/schema/payment-schema'
import { ampli } from '@/utils/ampli'
import { decamelizeKeys } from '@/utils/humps'

export const PurchaseEnterpriseModal: FunctionComponent = () => {
  const { formatMessage } = useIntl()
  const { userInfo } = useAuth()
  const isJapan = useIsJapan()

  const { data: userStatus } = useFetchUserStatusQuery()
  const [form] = Form.useForm<YearBuySchema>()

  const contactModal =
    reduxAPI.state.modal.hooks.useVisible('purchaseEnterprise')
  const [isPayYearLoading, setIsPayYearLoading] = useState<boolean>(false)
  const [isPayYearSuccess, setIsPayYearSuccess] = useState<boolean>(false)

  const handleSubmit = (values: YearBuySchema): void => {
    if (!userInfo) {
      return
    }

    setIsPayYearLoading(true)
    AppContext.ApiExecutor.planConsult(
      {
        ...values,
        email: userInfo.email,
        zipCode: 'N/A',
        address: 'N/A',
        type: 'buyEnterpriseQuota',
      },
      userStatus?.currentWorkspaceId,
    )
      .then(() => {
        setIsPayYearSuccess(true)
        form.resetFields()

        // (2025-05-27) MEMO: removed pipe drive contact event, because it's not used
        // pipedriveContactEvent(
        //   values.name,
        //   values.phone,
        //   userInfo.email,
        //   values.companyName,
        //   values.jobTitle,
        //   values.note,
        // )

        lineTagTracking('ContactUs')
        twitterPixelTracking('o76tn', 'Contact Us')

        ampli.submitEnterprisePlan({
          plan: 'enterprise',
          currency: userStatus?.currentWorkspace.currencyCode ?? undefined,
        })

        gtagApi.sendTo('-_5uCLD6-bUBEO67rPQC')
        fbq('track', 'Purchase', decamelizeKeys({ contentType: 'submitform' }))
        return noop()
      })
      .catch((error) => console.warn(error))
      .finally(() => {
        setIsPayYearLoading(false)
      })
  }

  return (
    <PlanConfigProvider>
      <StyledModal
        afterClose={(): void => {
          form.resetFields()
          setIsPayYearSuccess(false)
        }}
        footer={null}
        okButtonProps={{ style: { display: 'none' } }}
        open={contactModal.visible}
        style={{ top: '15vh' }}
        title={formatMessage({
          id: isJapan
            ? 'vip:contact_plan'
            : 'premium:section_cta_enterprise_btn',
        })}
        width={576}
        onCancel={(): void => {
          contactModal.toggleVisible()
        }}
      >
        {!isPayYearSuccess && (
          <Form form={form} layout='vertical' onFinish={handleSubmit}>
            <Descriptions>
              {formatMessage({ id: 'vip:plan_year_form_description' })}
            </Descriptions>
            <Form.Item
              initialValue={userInfo?.username}
              name='name'
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'error:field_agent_name_empty',
                  }),
                },
                {
                  min: 2,
                  message: formatMessage({
                    id: 'error:field_agent_name_empty',
                  }),
                },
                {
                  max: 50,
                  message: formatMessage({
                    id: 'error:field_agent_name_empty',
                  }),
                },
              ]}
              validateTrigger={['onBlur']}
            >
              <Input
                placeholder={`${formatMessage({
                  id: 'account:personal_info_field_username',
                })}*`}
              />
            </Form.Item>
            <Form.Item
              initialValue={userInfo?.company}
              name='companyName'
              rules={[
                {
                  required: true,
                  message: formatMessage({ id: 'error:company_fail' }),
                },
              ]}
              validateTrigger={['onBlur']}
            >
              <Input
                placeholder={`${formatMessage({
                  id: 'account:personal_info_field_company_name',
                })}*`}
              />
            </Form.Item>
            <Form.Item
              name='jobTitle'
              rules={[
                {
                  required: true,
                  message: formatMessage({ id: 'error:job_title_empty' }),
                },
              ]}
              validateTrigger={['onBlur']}
            >
              <Input
                placeholder={`${formatMessage({
                  id: 'vip:plan_year_field_job_title',
                })}*`}
              />
            </Form.Item>
            <Form.Item
              initialValue={userInfo?.phone}
              name='phone'
              rules={[
                {
                  required: true,
                  message: formatMessage({ id: 'error:phone_empty' }),
                },
              ]}
              validateTrigger={['onBlur']}
            >
              <Input
                placeholder={`${formatMessage({
                  id: 'consult:field_phone',
                })}*`}
              />
            </Form.Item>
            <Form.Item name='note'>
              <Input.TextArea
                autoSize={{ minRows: 5 }}
                placeholder={formatMessage({
                  id: 'vip:plan_year_field_note',
                })}
              />
            </Form.Item>

            <Form.Item>
              <Button
                block
                htmlType='submit'
                loading={isPayYearLoading}
                type='primary'
              >
                {formatMessage({ id: 'consult:field_req_submit_btn' })}
              </Button>
            </Form.Item>
          </Form>
        )}
        {isPayYearSuccess && (
          <SuccessMessage>
            <CheckCircleOutlined />
            <h2>{formatMessage({ id: 'general:form_submitted' })}</h2>
            <p>
              {formatMessage({ id: 'general:thanks_req_contact_you_later' })}
            </p>
          </SuccessMessage>
        )}
      </StyledModal>
    </PlanConfigProvider>
  )
}

const StyledModal = styled(BasicModal)`
  .ant-modal-title {
    text-align: center;
  }

  .ant-btn {
    height: 40px;
  }

  .anticon-check-circle {
    font-size: 3rem;
    color: ${({ theme }): string => theme.colors.plan.advanced};
    margin-bottom: 20px;
  }

  .ant-form {
    padding: 0 55px;
  }

  ${H3} {
    display: block;
    margin-bottom: 30px;
  }
`

const SuccessMessage = styled.div`
  text-align: center;
`

const Descriptions = styled.div`
  color: ${({ theme }): string => theme.colors.gray10};
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  margin: 0 0 25px;
`
